/**
 * Tiny SPA router for viisi-web.
 *
 * - history.pushState + popstate (no hash)
 * - param routes (`:id`) compiled to a regex
 * - exposes a reactive `currentRoute` via a subscribe() callback
 * - `navigate(path)` updates history and notifies subscribers
 * - the matching component is owned by `<Router>` (see components/Router.svelte)
 */

import type { Component } from 'svelte';

export interface RouteDef {
    path: string;
    component: Component<{ params?: Record<string, string> }>;
}

export interface CompiledRoute extends RouteDef {
    pattern: RegExp;
    paramNames: string[];
}

export interface RouteMatch {
    path: string;
    component: Component<{ params?: Record<string, string> }> | null;
    params: Record<string, string>;
    notFound: boolean;
}

function pathToRegex(path: string): RegExp {
    const escaped = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = escaped.replace(/:(\w+)/g, '([^/]+)');
    return new RegExp(`^${pattern}$`);
}

function extractParamNames(path: string): string[] {
    return Array.from(path.matchAll(/:(\w+)/g), (m) => m[1]);
}

export function compileRoutes(routes: RouteDef[]): CompiledRoute[] {
    return routes.map((r) => ({
        ...r,
        pattern: pathToRegex(r.path),
        paramNames: extractParamNames(r.path),
    }));
}

export function matchRoute(path: string, compiled: CompiledRoute[]): RouteMatch {
    for (const route of compiled) {
        const m = path.match(route.pattern);
        if (m) {
            const params: Record<string, string> = {};
            route.paramNames.forEach((name, i) => { params[name] = m[i + 1]; });
            return { path, component: route.component, params, notFound: false };
        }
    }
    return { path, component: null, params: {}, notFound: true };
}

type RouteListener = (path: string) => void;
const listeners = new Set<RouteListener>();

function emit(path: string): void {
    for (const cb of listeners) {
        try { cb(path); } catch (err) { console.error('[router] listener error:', err); }
    }
}

export function subscribe(cb: RouteListener): () => void {
    listeners.add(cb);
    cb(window.location.pathname);
    return () => { listeners.delete(cb); };
}

export function navigate(path: string, opts: { replace?: boolean } = {}): void {
    if (path === window.location.pathname + window.location.search) return;
    if (opts.replace) {
        history.replaceState({}, '', path);
    } else {
        history.pushState({}, '', path);
    }
    emit(window.location.pathname);
}

let listenersInstalled = false;

/**
 * Install global popstate + data-link click handlers. Idempotent.
 * Returns a teardown function.
 */
export function installRouterListeners(): () => void {
    if (listenersInstalled) return () => {};
    listenersInstalled = true;

    function onPopstate(): void {
        emit(window.location.pathname);
    }

    function onClick(e: MouseEvent): void {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const target = e.target as Element | null;
        const a = target?.closest('a[data-link]') as HTMLAnchorElement | null;
        if (!a) return;
        const href = a.getAttribute('href');
        if (!href) return;
        e.preventDefault();
        navigate(new URL(href, window.location.origin).pathname);
    }

    window.addEventListener('popstate', onPopstate);
    document.addEventListener('click', onClick);

    return () => {
        window.removeEventListener('popstate', onPopstate);
        document.removeEventListener('click', onClick);
        listenersInstalled = false;
    };
}
